<template>
  <div class="casseindex-container" @scroll="handleScroll" ref="containerRef">
    <!-- 顶部导航栏 -->
    <van-nav-bar :title="pageTitle" left-text="" left-arrow :border="false" class="custom-nav-bar"
      >
      <template #left>
        <Button type="back" @click="onClickLeft" />
      </template>
    </van-nav-bar>
    <!-- 固定说明文本 -->
    <div class="rules-section">
      <div class="rule-item">{{ $t('casseindex.level1PromoterRule') }}</div>
      <div class="rule-item">{{ $t('casseindex.level2AgentRule') }}</div>
      <div class="rule-item">{{ $t('casseindex.level3AgentRule') }}</div>
      <div class="rule-item">{{ $t('casseindex.level6AgentRule') }}</div>
      <div class="rule-item bold">{{ $t('casseindex.commissionAccumulationRule') }}</div>
    </div>

    <!-- 表格 -->
    <div class="table-wrap">
      <div class="table-header">
        <div class="th">{{ $t('casseindex.user') }}</div>
        <div class="th">{{ $t('casseindex.tier') }}</div>
        <div class="th">{{ $t('casseindex.bet') }}</div>
        <div class="th">{{ $t('casseindex.rebateReward') }}</div>
      </div>
      <template v-if="tableData.length">
        <div v-for="(row, idx) in tableData" :key="row.id" :class="['table-row', { zebra: idx % 2 === 1 }]">
          <div class="td">{{ maskUserName(row.userName) }}</div>
          <div class="td">{{ row.tair }}</div>
          <div class="td">{{ VITE_symbol }} {{ row.betAmount }}</div>
          <div class="td" style="color:#FEF64A;">{{ VITE_symbol }} {{ row.rebateReward }}</div>
        </div>
      </template>
      <template v-else>
        <div class="empty-wrap">
          <div class="flex justify-center items-center">
            <img :src="getAssetsFile('home/No_record@2x.png')" alt=""  class="max-h-[84px] max-w-[106.5px] w-auto h-auto">
          </div>
          <div class="empty-text">{{ $t('casseindex.noRecords') }}</div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from "@/components/Button"
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { getUserContribution } from '@/api/game'
import { http } from '@/utils/request'
import { useI18n } from 'vue-i18n'
const VITE_symbol = import.meta.env.VITE_SYMBOL
const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const containerRef = ref(null)

// 分页参数
const pageNo = ref(1)
const pageSize = ref(10)
const loading = ref(false)
const hasMore = ref(true)

// 从路由参数获取时间作为页面标题
const pageTitle = computed(() => {
  const startTime = route.query.startTime
  const endTime = route.query.endTime
  return startTime && endTime ? `${startTime} - ${endTime}` : t('casseindex.commissionDetails')
})

// 表格数据
const tableData = ref([])

// 格式化日期为 YYYY-MM-DD 格式
function formatDate(date: Date) {
  const y = date.getFullYear()
  const m = (date.getMonth() + 1).toString().padStart(2, '0')
  const d = date.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${d}`
}

// 用户名脱敏函数
function maskUserName(userName: string) {
  if (!userName || userName.length <= 2) return userName

  const firstChar = userName.charAt(0)
  const lastChar = userName.charAt(userName.length - 1)

  return `${firstChar}***${lastChar}`
}

// 滚动处理函数
function handleScroll(event: Event) {
  const target = event.target as HTMLElement
  const scrollTop = target.scrollTop
  const scrollHeight = target.scrollHeight
  const clientHeight = target.clientHeight

  // 当滚动到距离底部100px时触发加载
  if (scrollHeight - scrollTop - clientHeight < 100 && !loading.value && hasMore.value) {
    loadMore()
  }
}

// 加载更多数据
async function loadMore() {
  if (loading.value || !hasMore.value) return

  pageNo.value++
  await fetchTableData(true) // true表示追加数据
}

// 获取表格数据
async function fetchTableData(isAppend = false) {
  if (loading.value) return

  loading.value = true
  try {
    const startTime = route.query.startTime
    const endTime = route.query.endTime
    if (!startTime || !endTime) return

    // 构建基础参数
    const baseParams = `pageNo=${pageNo.value}&pageSize=${pageSize.value}&promoterId=4672&offset=0`

    // 直接使用传递过来的时间，不添加时分秒
    let url = `/center/user-promoter/getUserContribution?${baseParams}&time%5B0%5D=${startTime}&time%5B1%5D=${endTime}`

    console.log('请求URL:', url)

    // 直接使用 http.get 发送请求
    const response = await http.get(url)
    if (response && response.data) {
      const newData = response.data.list || []

      if (isAppend) {
        // 追加数据
        tableData.value = [...tableData.value, ...newData]
      } else {
        // 替换数据
        tableData.value = newData
      }

      // 判断是否还有更多数据
      hasMore.value = newData.length === pageSize.value
    }
  } catch (error) {
    console.error('获取数据失败:', error)
    console.error('错误详情:', error.response?.data)
    if (!isAppend) {
      tableData.value = []
    }
  } finally {
    loading.value = false
  }
}

// 重置分页
function resetPagination() {
  pageNo.value = 1
  hasMore.value = true
  tableData.value = []
}

const onClickLeft = () => {
  router.back()
}

onMounted(() => {
  resetPagination()
  fetchTableData()
})
</script>

<style lang="scss" scoped>
.casseindex-container {
  background: #154C37;
  min-height: 100vh;
  padding: 1px 0 0 0;
  border-radius: 8px 8px 0 0;
  overflow-y: auto;
  height: 100vh;
}

.custom-nav-bar {
  height: 56px;
  box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.3), 0px -2px 22px 0px rgba(53, 189, 128, 0.61);

  &>* {
    height: 100%;
  }
}

.rules-section {
  padding: 16px;
}

.rule-item {
  color: #5FB589;
  font-size: 10px;
  line-height: 1.5;
  margin-bottom: 8px;
}

.rule-item.bold {
  font-weight: bold;
  color: #fff;
}

.table-wrap {
  margin: 0 16px;
  overflow: hidden;
}

.table-header {
  display: flex;
  background: #006849;
  color: #fff;
  align-items: center;
  font-size: 10px;
  padding: 10px 0 8px 0;
}

.th {
  flex: 1;
  text-align: center;
}

.table-row {
  display: flex;
  background: #18553a;
  color: #b6e2d3;
  font-size: 10px;
  padding: 14px 0;
  align-items: center;
  transition: background 0.2s;
}

.table-row.zebra {
  background: #176343;
}

.td {
  flex: 1;
  text-align: center;
}

.empty-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 115px 0 0 0;
}


.empty-text {
  color: #ADB1B0;
  font-size: 11px;
  margin-top: 21px;

}
</style>

<route lang="json5">
{
  name: 'casseindex',
  path: '/casseindex',
  meta: {
    title: 'Commission Details',
    i18n: 'menus.casseindex'
  },
}
</route>
